import { AxiosError, AxiosResponse } from 'axios';
import { action, computed, observable } from 'mobx';
import { stringify } from 'qs';
import { ListboxValue } from 'telia-front-react';
import { MandateCustomer, Notice, UiResponse } from '../CommonInterfaces';
import { LARES_POST_FORM_PATH, UI_WEB_API_PATH, View } from '../Routes';
import { API, ErrorResponse, FetchStatus } from '../util/ApiUtil';
import ErrorUtil from '../util/ErrorUtil';
import { resolveRedirectUrl } from '../util/UiResponseUtil';
import RootStore from './RootStore';
import RouteStore from './RouteStore';
import UiStore from './UiStore';
import PasskeyStore from './PasskeyStore';

export interface MandatesResponse extends UiResponse {
  b2bCustomers: MandateCustomer[];
  b2cCustomers: MandateCustomer[];
  registrableCompanies?: boolean;
}

const POSTMESSAGE_ACTION = 'mandates-loaded';

export default class MandateStore {
  @observable accessor status: FetchStatus = FetchStatus.UNINITIALIZED;
  @observable accessor notice: Notice | null = null;
  @observable accessor errorCode: string = '';
  @observable accessor b2bCustomers: MandateCustomer[] = [];
  @observable accessor b2cCustomers: MandateCustomer[] = [];
  @observable accessor registrableCompanies: boolean = false;
  @observable accessor softProfile: boolean = false;

  private routeStore: RouteStore;
  private uiStore: UiStore;
  private passkeyStore: PasskeyStore;

  constructor(rootStore: RootStore) {
    this.routeStore = rootStore.routeStore;
    this.uiStore = rootStore.uiStore;
    this.passkeyStore = rootStore.passkeyStore;
  }

  @computed
  public get isFetching() {
    return this.status === FetchStatus.FETCHING || this.status === FetchStatus.UNINITIALIZED;
  }

  @computed
  public get isFetchingMore() {
    return this.status === FetchStatus.FETCHING_MORE;
  }

  @computed
  public get isError() {
    return this.status === FetchStatus.ERROR;
  }

  @computed
  public get isContinueFetching() {
    return this.status === FetchStatus.FETCHING_MORE;
  }

  @computed
  get hideB2B() {
    return /true/i.test(this.routeStore.decodedParams.hideB2B || '');
  }

  @computed
  get hideLogout() {
    return /true/i.test(this.routeStore.decodedParams.hideLogout || '');
  }

  @computed
  public get selectedMandateValue(): ListboxValue {
    const b2cMandate = this.b2cCustomers.find((customer) => customer.selected);
    if (b2cMandate) {
      return `b2c-mandate-list-item-${this.b2cCustomers.indexOf(b2cMandate)}`;
    }
    const b2bMandate = this.b2bCustomers.find((customer) => customer.selected);
    if (b2bMandate) {
      return `b2b-mandate-list-item-${this.b2bCustomers.indexOf(b2bMandate)}`;
    }
    return null;
  }

  @action.bound
  public loadMandates() {
    this.status = FetchStatus.FETCHING;
    this.notice = null;
    return API.get(`/mandate${this.routeStore.formForwardParams}`).then(this.handleLoadSuccess).catch(this.handleLoadError);
  }

  @action.bound
  public async selectMandate(mandateIndex: number) {
    const hasAlreadySelectedMandate = this.selectedMandateValue !== null;
    this.status = FetchStatus.FETCHING;
    try {
      const response = await API.post(`/mandate/${mandateIndex}${this.routeStore.formForwardParams}`);
      hasAlreadySelectedMandate ? this.handleActionSuccess(response) : await this.passkeyStore.handleOfferRegisterModalLogic(() => {
        this.handleActionSuccess(response);
      });
    } catch(e: any) {
      this.handleActionError(e);
    }  
  }

  @action.bound
  public registerCompany() {
    this.status = FetchStatus.FETCHING;
    return API.get('/mandate/registerB2b').then(this.handleActionSuccess).catch(this.handleActionError);
  }

  @action.bound
  public continueFailedMandates = () => {
    this.status = FetchStatus.FETCHING_MORE;
    return API.post(`/mandate/continue${this.routeStore.formForwardParams}`).then(this.handleContinueSuccess).catch(this.handleContinueError);
  };

  @action.bound
  private handleLoadSuccess(response: AxiosResponse) {
    const uiResponse: MandatesResponse = response.data;
    const redirectUrl = resolveRedirectUrl(uiResponse, this.routeStore.formForwardParams);
    if (redirectUrl) {
      return this.routeStore.route(redirectUrl);
    }
    this.b2bCustomers = uiResponse.b2bCustomers || [];
    this.b2cCustomers = uiResponse.b2cCustomers || [];
    this.registrableCompanies = uiResponse.registrableCompanies || false;
    this.uiStore.handleNotice(response);
    this.status = FetchStatus.FETCHED;
    window.parent.postMessage({ action: POSTMESSAGE_ACTION, data: { action: POSTMESSAGE_ACTION } }, '*');
  }

  @action.bound
  private handleLoadError(e: AxiosError) {
    ErrorUtil.pushErrorToTeliaRumAndLog(e);
    const data: ErrorResponse = e.response?.data as ErrorResponse;
    if (!!data) {
      const redirectUrl = resolveRedirectUrl(data, this.routeStore.formForwardParams);
      if (redirectUrl) {
        return this.routeStore.route(redirectUrl);
      }
      if (data.softProfile) {
        this.softProfile = data.softProfile;
      }
    }
    this.uiStore.handleNotice(e.response);
    this.status = FetchStatus.ERROR;
  }

  @action.bound
  private handleActionSuccess(response: AxiosResponse) {
    const uiResponse: MandatesResponse = response.data;

    if (uiResponse.redirectUrl) {
      if (!uiResponse.redirectUrl.startsWith('/')) {
        return (window.top!.location.href = uiResponse.redirectUrl);
      } else {
        const redirectUrl = resolveRedirectUrl(uiResponse, this.routeStore.formForwardParams);
        if (redirectUrl) {
          return this.routeStore.route(redirectUrl);
        }
      }
    }

    const params = this.routeStore.decodedParams || {};

    if (uiResponse.component === View.WEB_API || params.webapi || params.goto === 'webapi') {
      const webapiQueryString = stringify(
        {
          mandateChanged: true,
          allowStatisticsCookies: this.routeStore.getParameter('allowStatisticsCookies'),
          goto: params.goto,
        },
        { addQueryPrefix: true },
      );
      return this.routeStore.route(UI_WEB_API_PATH + webapiQueryString);
    }

    if (uiResponse.component === View.LARES) {
      return this.routeStore.route(LARES_POST_FORM_PATH + this.routeStore.formForwardParamsAnd(uiResponse.params || {}));
    }

    this.status = FetchStatus.FETCHED;
    this.uiStore.handleNotice(response);
  }

  @action.bound
  private handleActionError(e: AxiosError) {
    ErrorUtil.pushErrorToTeliaRumAndLog(e);
    const data = e.response?.data;
    const redirectUrl = !!data ? resolveRedirectUrl(data, this.routeStore.formForwardParams) : null;
    if (redirectUrl) {
      return this.routeStore.route(redirectUrl);
    }
    this.uiStore.handleNotice(e.response);
    this.status = FetchStatus.ERROR;
  }

  @action.bound
  private handleContinueSuccess(response: AxiosResponse<UiResponse>) {
    if (!!response.data.redirectUrl) {
      return this.routeStore.route(response.data.redirectUrl);
    }
    if (response.data.component === View.WEB_API || this.routeStore.decodedParams?.webapi || this.routeStore.decodedParams?.goto === 'webapi') {
      const webapiQueryString = stringify(
        { mandateChanged: true, allowStatisticsCookies: this.routeStore.getParameter('allowStatisticsCookies') },
        { addQueryPrefix: true },
      );
      return this.routeStore.route(UI_WEB_API_PATH + webapiQueryString);
    }
    this.uiStore.handleNotice(response);
    this.status = FetchStatus.FETCHED;
  }

  @action.bound
  private handleContinueError(e: AxiosError) {
    ErrorUtil.pushErrorToTeliaRumAndLog(e);
    this.status = FetchStatus.ERROR;
    this.uiStore.handleNotice(e.response);
    this.routeStore.route(this.routeStore.homePageWithParams);
  }
}
